import { useEffect, useMemo } from 'react'

import { usePageShow, useTrackingInit } from '@lib/hooks'
import { useMailRedirect } from '@lib/hooks/use-mail-redirect'
import { usePersistMailExtension } from '@lib/hooks/use-persist-mail-extension'
import { withCurrentUser } from '@lib/mixins/with-current-user'
import { getPageIdFromSlug } from '@lib/utils/slugs'

import { type TypeSeoMetadataEntry, type TypeSocialMetadataEntry } from '@contentful-types/index'

import { type PageComponentProps } from '@typings/components/page'

import { HeroImageProvider } from '@contexts/hero-image-context'

import { PageShell } from '@components/page-shell/page-shell'

import { getCustomPageComponent } from '@custom/registry/utils'

interface CustomPageProps extends Omit<PageComponentProps, 'data'> {
  data?: PageComponentProps['data'] | undefined
  slug: string
  heroImageSeed?: number | null
  metadata: {
    social?: TypeSocialMetadataEntry
    seo?: TypeSeoMetadataEntry
  }
  isPreview?: boolean
  enabledFlags?: string[]
}

const CustomPage = withCurrentUser(
  ({ slug, data, metadata, isPreview, heroImageSeed, enabledFlags }: CustomPageProps) => {
    const pageId = getPageIdFromSlug(slug)
    const { shouldRedirectToMail, redirectToMail } = useMailRedirect(slug, pageId)

    // Get the custom page component (static import for SSR compatibility)
    const CustomPageComponent = getCustomPageComponent(slug)

    useTrackingInit(pageId, slug)
    usePageShow({ pageId, pageSlug: slug, isPreview, isCustom: true })
    usePersistMailExtension()

    useEffect(() => {
      if (shouldRedirectToMail) {
        void redirectToMail()
      }
    }, [shouldRedirectToMail, redirectToMail])

    const contextValue = useMemo(
      () => ({
        pageId,
        slug,
        isPreview,
      }),
      [pageId, slug, isPreview],
    )

    // If no custom page component is found, return null or error state
    const pageContent = CustomPageComponent ? (
      <CustomPageComponent
        slug={slug}
        data={data}
        isPreview={isPreview}
        enabledFlags={enabledFlags}
      />
    ) : (
      <div>Custom page not found for slug: {pageId.replace('-custom-page', '')}</div>
    )

    return (
      <PageShell pageContext={contextValue} metadata={metadata} heroImageSeed={heroImageSeed}>
        <HeroImageProvider heroImageSeed={heroImageSeed}>{pageContent}</HeroImageProvider>
      </PageShell>
    )
  },
)

export default CustomPage
